import { useSelector } from "react-redux";
import { RootState } from "core/src/store/store.types";
import { createSelector } from "@reduxjs/toolkit";
import { sortUser } from "etl/utils";
import { ExtractLaboratory, LoadingStatusEnum } from "etl/types";
import { useLazyGetSelectedEtlImportQuery } from "etl/core";

const getEtlState = (state: RootState) => state.etlData;

const getAppIsInitialized = createSelector(getEtlState, (state) => state.appIsInitialized);

// Dictionnary
const getDictionaryBrands = createSelector(getEtlState, (state) => state.dictionaryBrands ?? []);
const getDictionarySuppliers = createSelector(getEtlState, (state) => state.dictionarySuppliers ?? []);
const getDictionaryCategories = createSelector(getEtlState, (state) => state.dictionaryCategories ?? []);
const getDictionaryProducts = createSelector(getEtlState, (state) => state.dictionaryProducts ?? []);
const getDictionaryScheduleTypes = createSelector(getEtlState, (state) => state.dictionaryScheduleTypes ?? []);
const getDictionaryLaboratories = createSelector(getEtlState, (state) => state.dictionaryLaboratories ?? []);

// Extract
const getExtractUsers = createSelector(getEtlState, (state) => state.extractUsers ?? []);
const getExtractLaboratories = createSelector(getEtlState, (state) => [...state.extractLaboratories].sort((a, b) => Number(a.aw_laboratory_id) - Number(b.aw_laboratory_id)) ?? []);
const getDictionaryUsers = createSelector(getEtlState, (state) => [...state.dictionaryUsers].sort(sortUser) ?? []);
const getExtractBrands = createSelector(getEtlState, (state) => state.extractBrands ?? []);
const getExtractSuppliers = createSelector(getEtlState, (state) => state.extractSuppliers ?? []);
const getExtractCategories = createSelector(getEtlState, (state) => state.extractCategories ?? []);
const getExtractProducts = createSelector(getEtlState, (state) => state.extractProducts ?? []);
const getExtractScheduleTypes = createSelector(getEtlState, (state) => state.extractScheduleTypes ?? []);

const getSelectedEtlImportUuid = createSelector(getEtlState, (state) => state.selectedEtlImportUuid ?? "");

export const useEtlDataSelectors = () => {

	const appIsInitialized = useSelector(getAppIsInitialized);

	const selectedEtlImportUuid = useSelector(getSelectedEtlImportUuid);

	const [getSelectedEtlImport,
		{ data: selectedEtlImport, isLoading: isLoadingEtlImport, isFetching: isFetchingEtlImport, isSuccess: isSuccessEtlImport, isError: isErrorEtlImport }
	] = useLazyGetSelectedEtlImportQuery();

	if (
		selectedEtlImportUuid
		&& !isLoadingEtlImport
		&& !isFetchingEtlImport
		&& !isSuccessEtlImport
		&& !isErrorEtlImport
	) getSelectedEtlImport({ uuid: selectedEtlImportUuid });

	const usersIsLoading = useSelector((state: RootState) => state.etlData.usersLoadingStatus === LoadingStatusEnum.Loading);
	const brandsIsLoading = useSelector((state: RootState) => state.etlData.brandsLoadingStatus === LoadingStatusEnum.Loading);
	const suppliersIsLoading = useSelector((state: RootState) => state.etlData.suppliersLoadingStatus === LoadingStatusEnum.Loading);
	const categoriesIsLoading = useSelector((state: RootState) => state.etlData.categoriesLoadingStatus === LoadingStatusEnum.Loading);
	const productsIsLoading = useSelector((state: RootState) => state.etlData.productsLoadingStatus === LoadingStatusEnum.Loading);
	const scheduleTypesIsLoading = useSelector((state: RootState) => state.etlData.scheduleTypesLoadingStatus === LoadingStatusEnum.Loading);
	const laboratoriesIsLoading = useSelector((state: RootState) => state.etlData.laboratoriesLoadingStatus === LoadingStatusEnum.Loading);

	const dictionaryUsers = useSelector(getDictionaryUsers);
	const dictionaryBrands = useSelector(getDictionaryBrands);
	const dictionarySuppliers = useSelector(getDictionarySuppliers);
	const dictionaryCategories = useSelector(getDictionaryCategories);
	const dictionaryProducts = useSelector(getDictionaryProducts);
	const dictionaryScheduleTypes = useSelector(getDictionaryScheduleTypes);
	const dictionaryLaboratories = useSelector(getDictionaryLaboratories);

	const extractProducts = useSelector(getExtractProducts);
	const extractCategories = useSelector(getExtractCategories);
	const extractBrands = useSelector(getExtractBrands);
	const extractSuppliers = useSelector(getExtractSuppliers);
	const extractScheduleTypes = useSelector(getExtractScheduleTypes);
	const extractUsers = useSelector(getExtractUsers);
	const extractLaboratories = useSelector(getExtractLaboratories);

	return {
		// App
		appIsInitialized,

		// Etl Imports
		selectedEtlImportUuid,
		selectedEtlImport,
		isLoadingEtlImport,
		isFetchingEtlImport,
		isSuccessEtlImport,
		isErrorEtlImport,

		// Laboratories
		getLaboratoryById: (id: string) => extractLaboratories.find((laboratory: ExtractLaboratory) => laboratory.aw_laboratory_id == id),

		unzipSummary: selectedEtlImport?.meta_data?.unzipSummary ?? {},

		// Loading status
		usersIsLoading,
		brandsIsLoading,
		suppliersIsLoading,
		categoriesIsLoading,
		productsIsLoading,
		scheduleTypesIsLoading,
		laboratoriesIsLoading,

		// Dictionnary
		dictionaryUsers,
		dictionaryBrands,
		dictionarySuppliers,
		dictionaryCategories,
		dictionaryProducts,
		dictionaryScheduleTypes,
		dictionaryLaboratories,

		// Extract
		extractProducts,
		extractCategories,
		extractBrands,
		extractSuppliers,
		extractScheduleTypes,
		extractUsers,
		extractLaboratories,
	};
};

export type UseEtlDataSelectors = ReturnType<typeof useEtlDataSelectors>;
